import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import * as logger from '../../../logger';

import defaultStyles from '../../../styles/formSetStyles.module.css';
import registrationStyles from '../../../scenes/Login/Registration/index.module.css';
import styles from '../index.module.css';

import * as api from '../../../api';

import { Button } from '../../Button';
import { ModalContainer } from '../../ModalContainer';
import PhoneInput from '../../PhoneInput';

import { isNumberValid } from '../../../utils/phoneNumber';

const ALERT_MESSAGE =
  'Failed to submit call back request, please contact your care provider.';

interface Props {
  navigateToNextPage: () => Promise<void>;
  practice: api.Practice.Practice;
  previsitFormFieldId: string;
  label?: string;
}

const CallSection: React.FC<Props> = ({
  navigateToNextPage,
  practice,
  previsitFormFieldId,
  label,
}) => {
  const { t } = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [patientCallbackRequested, setPatientCallbackRequested] =
    useState<boolean>(false);

  const createPatientCallbackRequest = async () => {
    if (!phoneNumber || !isNumberValid(phoneNumber)) {
      alert(t(ALERT_MESSAGE));
      return;
    }

    setIsSubmitting(true);

    try {
      const patientCallbackRequest =
        await api.client.Previsit.createPatientCallbackRequest({
          previsitFormFieldId,
          phoneNumber,
        });
      if (patientCallbackRequest) setPatientCallbackRequested(true);
    } catch (e) {
      logger.error(
        e,
        `CallSection.createPatientCallbackRequest : Failed to submit call back request`,
      );
      alert(t(ALERT_MESSAGE));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderModal = () => (
    <ModalContainer
      contentClassName={registrationStyles.ModalContainer}
      isOpen={isModalOpen}
      onModalClose={() => setIsModalOpen(false)}
      topLabel={t('Request a call back')}
    >
      <div
        className={classNames(
          registrationStyles.Inputs,
          registrationStyles.RegistrationInputs,
        )}
      >
        {patientCallbackRequested ? (
          <>
            <div
              className={classNames(
                defaultStyles.SubLabel,
                registrationStyles.SubLabel,
              )}
            >
              <strong>
                {t(
                  'Your call back is confirmed. We will give you a call as soon as possible.',
                )}
              </strong>
            </div>
            <Button
              type="button"
              onClick={navigateToNextPage}
              children={t('Continue')}
            />
          </>
        ) : (
          <>
            <div
              className={classNames(
                defaultStyles.SubLabel,
                registrationStyles.SubLabel,
              )}
            >
              <strong>{t('pleaseAddPrimaryPhoneNumberForCallBack')}</strong>
            </div>
            <div className={styles.PhoneInputDiv}>
              <PhoneInput
                label={t('pleaseAddPrimaryPhoneNumberForCallBack')}
                className={classNames(
                  registrationStyles.Input,
                  styles.PhoneInput,
                )}
                value={phoneNumber || ''}
                placeholder={t('phoneNumber')}
                saveFieldValue={() => {}}
                updateFieldValue={setPhoneNumber}
              />
            </div>
            <Button
              type="button"
              onClick={createPatientCallbackRequest}
              children={isSubmitting ? t('Loading...') : t('Continue')}
              disabled={!isNumberValid(phoneNumber || '') || isSubmitting}
            />
          </>
        )}
      </div>
    </ModalContainer>
  );

  return (
    <div className={styles.CallSection}>
      {label && <div className={styles.CallSectionLabel}>{label}</div>}
      <div className={styles.CallButton}>
        <Button
          type="button"
          onClick={() => (window.location.href = `tel:${practice.phoneNumber}`)}
        >
          {t('Call now')}
        </Button>
        <Button
          type="button"
          onClick={() => setIsModalOpen(true)}
          children={t('Request a call back')}
        />
      </div>
      {renderModal()}
    </div>
  );
};

export default CallSection;
